import { DocumentNode, print } from 'graphql';

type Options = {
  query: DocumentNode;
  field: string;
  variables: Record<string, any>;
  headers?: Record<string, string>;
  authToken?: string;
};

/**
 * Sends a GraphQL request to the specified endpoint.
 * @function sendGraphqlRequest
 * @template T - The type of the response data.
 * @param {Options} options - The options for the GraphQL request.
 * @param {DocumentNode} options.query - The GraphQL query document.
 * @param {string} options.field - The field to extract from the response data.
 * @param {Record<string, any>} options.variables - The variables for the GraphQL query.
 * @param {Record<string, string>} [options.headers] - Optional headers for the request.
 * @param {string} [options.authToken] - Optional authorization token for the request.
 * @returns {Promise<T>} A promise that resolves to the response data.
 */
export const sendGraphqlRequest = async <T = unknown>({
  query,
  field,
  variables,
  headers,
  authToken,
}: Options): Promise<T> => {
  const response = await fetch(process.env.NEXT_PUBLIC_GQL_API_URL ?? '', {
    method: 'POST',
    headers: {
      ...headers,
      'Content-Type': 'application/json',
      Authorization: authToken ? `Bearer ${authToken}` : '',
    },
    body: JSON.stringify({
      query: print(query),
      variables,
    }),
  });
  const result = await response.json();
  return result.data[field];
};
